import React, { useRef, useEffect } from 'react'
import { useStateValue } from '@State'
import { uploadFile, deleteFile } from '../../api'
import { TabSplitLayout } from './utils/TabSplitLayout'
import { FeedbackDisplay } from './utils/FeedbackDisplay'

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const MAX_CLIENT_SIZE = 150 * 1024 * 1024 // 150 MB

export const CodeGraderQuestion = ({ question, renderTabs = true }) => {
  const [state, dispatch] = useStateValue()
  const { coursenum, activitySubmissionId, readonly } = state
  const fileInputRef = useRef(null)

  const { guid, text, gradePoints } = question
  const {
    files, maxRequired,
    uploadingFile, uploadProgress, uploadError,
    graderFeedback,
  } = question.data

  useEffect(() => {
    const selected = (question.data.files || []).length
    const { minSelections, maxSelections } = question.data
    dispatch({
      type: 'SET_FULLY_ANSWERED', guid: question.guid,
      value: selected >= minSelections && selected <= maxSelections
    })
  }, [question.data.files])

  const isGraded = gradePoints !== null && gradePoints !== undefined
  const atMax = files.length >= maxRequired

  const handleFileSelect = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    e.target.value = ''

    if (file.size > MAX_CLIENT_SIZE) {
      dispatch({ type: 'FILE_UPLOAD_ERROR', guid, error: 'File exceeds maximum size of 150 MB' })
      return
    }
    if (files.length >= maxRequired) {
      dispatch({ type: 'FILE_UPLOAD_ERROR', guid, error: 'Maximum number of files reached' })
      return
    }

    dispatch({ type: 'FILE_UPLOAD_START', guid, filename: file.name })

    try {
      const response = await uploadFile(
        coursenum, activitySubmissionId, guid, file,
        (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100)
            dispatch({ type: 'FILE_UPLOAD_PROGRESS', guid, percent })
          }
        }
      )
      const data = response.data
      if (data.success) {
        dispatch({
          type: 'FILE_UPLOAD_SUCCESS',
          guid,
          file: data.file,
          isAnswered: data.is_answered,
          gradePoints: data.grade_points,
        })
      } else {
        dispatch({ type: 'FILE_UPLOAD_ERROR', guid, error: data.error || 'Upload failed' })
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Upload failed'
      dispatch({ type: 'FILE_UPLOAD_ERROR', guid, error: msg })
    }
  }

  const handleDelete = async (fileId) => {
    try {
      const response = await deleteFile(coursenum, activitySubmissionId, guid, fileId)
      const data = response.data
      if (data.success) {
        dispatch({
          type: 'FILE_DELETE_SUCCESS',
          guid,
          fileId,
          isAnswered: data.is_answered,
          gradePoints: data.grade_points,
        })
      }
    } catch (err) {
      // Silently fail delete
    }
  }

  return (
    <>
      <FeedbackDisplay question={question}>
        {graderFeedback && (
          <div style={{ whiteSpace: 'pre-wrap' }}>{graderFeedback}</div>
        )}
      </FeedbackDisplay>
      <TabSplitLayout question={question} renderTabs={renderTabs}>
      <div className="d-flex flex-column align-items-start">
        <div
          role="heading"
          aria-level={3}
          className="mb-2"
          dangerouslySetInnerHTML={{ __html: text }}
        />

        {/* File list */}
        {files.length > 0 && (
          <ul className="list-unstyled w-100 mb-2">
            {files.map(f => (
              <li
                key={f.id}
                className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-1"
              >
                <div>
                  <span className="fa-solid fa-file-code mr-2 text-muted" />
                  <span>{f.filename}</span>
                  <span className="text-muted ml-2 small">({formatFileSize(f.size)})</span>
                </div>
                {!readonly && !isGraded && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(f.id)}
                    title="Remove file"
                  >
                    <span className="fa-solid fa-trash-alt" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Upload area */}
        {!readonly && !atMax && !isGraded && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              className="d-none"
              onChange={handleFileSelect}
            />
            {uploadingFile ? (
              <div className="w-100 mb-2">
                <div className="small text-muted mb-1">Uploading {uploadingFile}...</div>
                <div className="progress" style={{ height: '20px' }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: uploadProgress + '%' }}
                    aria-valuenow={uploadProgress}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  >
                    {uploadProgress}%
                  </div>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <span className="fa-solid fa-upload mr-1" />
                Upload Code File
              </button>
            )}
          </>
        )}

        {/* Info message when no file and not graded */}
        {files.length === 0 && !uploadingFile && (readonly || isGraded) && (
          <div className="text-muted small">
            <span className="fa-solid fa-laptop-code mr-1" aria-hidden="true" />
            No file uploaded.
          </div>
        )}

        {/* Error display */}
        {uploadError && (
          <div className="alert alert-danger mt-2 py-1 px-2 small w-100">
            {uploadError}
          </div>
        )}

        {/* Pre-grading hint */}
        {!isGraded && files.length > 0 && (
          <div className="text-muted small mt-2">
            <span className="fa-solid fa-laptop-code mr-1" aria-hidden="true" />
            This file will be graded automatically when the assessment is submitted.
          </div>
        )}
      </div>
      </TabSplitLayout>
    </>
  )
}
